// WHY — per-account drilldown. Status-led header with the 8-metric tile grid
// (B11) and an ad-set table (B12). Range (30/60/90) lives in the URL so it's
// shareable. All hooks run before any conditional rendering to satisfy Rules
// of Hooks.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Card } from "../components/Card";
import { Banner } from "../components/Banner";
import { StatusDot } from "../components/StatusDot";
import { RangeToggle } from "../components/RangeToggle";
import { MetricTileGrid } from "../components/MetricTileGrid";
import { AdSetTable, type AdSetRow } from "../components/AdSetTable";
import { api } from "../lib/api";
import type { AccountStatus, Range } from "../lib/types";

interface MetricSeries {
  value_7d: number | null;
  delta_pct: number | null;
  sparkline: Array<{ date: string; value: number | null }>;
  alert_active: boolean;
}

interface AccountResponse {
  account: {
    account_id: string;
    display_name: string;
    currency: string;
    timezone: string;
    status: AccountStatus;
  };
  metrics: {
    cpl: MetricSeries;
    leads: MetricSeries;
    spend: MetricSeries;
    ctr: MetricSeries;
    cpm: MetricSeries;
    conv_rate: MetricSeries;
    impressions: MetricSeries;
    clicks: MetricSeries;
  };
  adsets: AdSetRow[];
}

function readRangeFromURL(): Range {
  if (typeof window === "undefined") return 90;
  const raw = new URLSearchParams(window.location.search).get("range");
  if (raw === "30") return 30;
  if (raw === "60") return 60;
  if (raw === "90") return 90;
  return 90;
}

function writeRangeToURL(range: Range): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("range", String(range));
  window.history.replaceState(null, "", url.toString());
}

function adsManagerUrl(accountId: string): string {
  const numeric = accountId.replace(/^act_/, "");
  return `https://business.facebook.com/adsmanager/manage/campaigns?act=${numeric}`;
}

export function AccountPage() {
  const { accountId } = useParams<{ accountId: string }>();
  const [range, setRange] = useState<Range>(() => readRangeFromURL());
  const [data, setData] = useState<AccountResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!accountId) return;
    writeRangeToURL(range);
    setData(null);
    setErr(null);
    setNotFound(false);
    let cancelled = false;
    api<AccountResponse>(`/api/account/${encodeURIComponent(accountId)}?range=${range}`)
      .then((resp) => {
        if (!cancelled) setData(resp);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { status?: number }).status;
        if (status === 404) {
          setNotFound(true);
          return;
        }
        const msg = e instanceof Error ? e.message : "Failed to load";
        setErr(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, range]);

  return (
    <Layout>
      {notFound ? (
        <Card>
          <h2>Account not found</h2>
          <p style={{ color: "var(--color-bone-dim)" }}>
            That account ID doesn't belong to your client.{" "}
            <a href="/dashboard">Back to dashboard</a>
          </p>
        </Card>
      ) : (
        <>
          <p style={{ marginBottom: "var(--space-3)" }}>
            <a
              href="/dashboard"
              style={{
                color: "var(--color-bone-dim)",
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-xs)",
                letterSpacing: "var(--letter-label)",
                textTransform: "uppercase",
              }}
            >
              ← All accounts
            </a>
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              marginBottom: "var(--space-5)",
            }}
          >
            <div>
              <span className="label">
                {data
                  ? `${data.account.account_id} · ${data.account.currency} · ${data.account.timezone}`
                  : ""}
              </span>
              <h1 style={{ marginTop: "6px" }}>
                {data && <StatusDot status={data.account.status} />}
                {data?.account.display_name ?? "…"}
              </h1>
            </div>
            <RangeToggle value={range} onChange={setRange} />
          </div>

          {err && <Banner tone="err">{err}</Banner>}
          {data === null && !err && <Banner tone="info">Loading…</Banner>}

          {data && (
            <>
              <div style={{ marginBottom: "var(--space-5)" }}>
                <MetricTileGrid
                  currency={data.account.currency}
                  metrics={data.metrics}
                />
              </div>

              <Card>
                <span
                  className="label"
                  style={{ display: "block", marginBottom: "var(--space-3)" }}
                >
                  Ad sets · last 7 days
                </span>
                <AdSetTable
                  rows={data.adsets}
                  currency={data.account.currency}
                />
              </Card>

              <p
                style={{
                  marginTop: "var(--space-4)",
                  fontSize: "var(--fs-sm)",
                  color: "var(--color-bone-dim)",
                }}
              >
                <a
                  href={adsManagerUrl(data.account.account_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "var(--color-bone-dim)" }}
                >
                  Open in Meta Ads Manager →
                </a>
              </p>
            </>
          )}
        </>
      )}
    </Layout>
  );
}
